// src/features/release-notes/components/ReleaseNotesDialog.tsx

import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CloseRounded as CloseIcon } from '@mui/icons-material';
import { useReleaseNoteActions } from '../useReleaseNoteActions';
import type { ReleaseNote } from '../types';
import { ReleaseNoteCard } from './ReleaseNoteCard';

export interface ReleaseNotesDialogProps {
  notes: ReleaseNote[];
  /** Acknowledges every note shown and closes. */
  onAcknowledge: () => void;
  /** Closes without acknowledging (the cross, Escape, the backdrop). */
  onDismiss: () => void;
}

/**
 * «Novedades» at sign-in. «Entendido» or following a note acknowledges all shown; the cross
 * dismisses without acknowledging. Full-screen on a phone; the body is the only scroller and the
 * button stays in sight.
 */
export const ReleaseNotesDialog: React.FC<ReleaseNotesDialogProps> = ({ notes, onAcknowledge, onDismiss }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { openScreen, openManual } = useReleaseNoteActions(onAcknowledge);
  const titleId = 'release-notes-title';

  return (
    <Dialog open onClose={onDismiss} fullScreen={fullScreen} fullWidth maxWidth="sm" scroll="paper" aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
        {translate('releaseNotes.dialog.title')}
        <IconButton aria-label={translate('releaseNotes.close')} onClick={onDismiss} sx={{ width: 44, height: 44 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate('releaseNotes.dialog.intro')}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {notes.map((note) => (
            <li key={note.id}>
              <ReleaseNoteCard note={note} onOpenScreen={openScreen} onOpenManual={openManual} />
            </li>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" onClick={onAcknowledge} sx={{ minHeight: 44 }}>
          {translate('releaseNotes.gotIt')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
